import { uniqBy } from "lodash";
import { Inertia } from "@inertiajs/inertia";

import {Trans} from "../utils/i18n";
import { hasProducts } from "./orderEntryEnfore";

export const shopAmazonConfig = {
    sizes: {
        forth: 3,
        third: 4,
        half: 6,
        full: 12,
    },
    enableStepper: true,
    defaultValues: {
        productcategory: "all",
    },
    heading: <Trans i18nKey="orderEntry.basicData">Basic data</Trans>,
    groups: [
        {
            heading: (
                <Trans i18nKey="orderEntry.productSelection">
                    Product selection
                </Trans>
            ),
            description: (
                <Trans i18nKey="orderEntry.productSelection.description"></Trans>
            ),
            id: "product_selection",
            fields: [
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3 className={"mt-9"} style={{marginBottom: 0}}>
                                <Trans i18nKey={"shop.serialNumber"}>
                                    Enter the serial number of your product
                                    (optional)
                                </Trans>
                            </h3>
                        </>
                    ),
                },
                {
                    component: "serialnumber",
                    "shopMode": true,
                    size: "full",
                    required: true,
                    validateSerialNumber: (value) =>
                        !value || /^.{16}$/.test(value) ? undefined : (
                            <Trans i18nKey="serialNumber.write16chars">
                                You have to provide exactly 16 characters
                            </Trans>
                        ),
                    uppercase: true,
                    doNotShowConfirmDevice: true,
                },
                {
                    size: "full",
                    component: "productcategoryselector",
                    showAll: false,
                    queryOptions: {
                        queryParameters: {
                            include: ["products"],
                            filter: {
                                "products.type": "unit",
                                is_for_sales: true,
                            },
                        }
                    }
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3 className={"mt-9"} style={{marginBottom: 0}}>
                                <Trans i18nKey={"shop.productSelect"}>
                                    Or select your model
                                </Trans>
                            </h3>
                        </>
                    ),
                    conditionFunction: (values) => {
                        return !!values.productcategory;
                    },
                },
                {
                    size: "full",
                    component: "productmodelgroupselector",
                    conditionFunction: (values) => {
                        return !!values.productcategory;
                    },
                },
                ({ dispatch, state, form, send, values, queryClient }) => ({
                    size: "full",
                    component: "productvariantselector",
                    name: "variant",
                    queryOptions: {
                        queryParameters: {
                            filter: {
                                    model_group: values.model_group,
                                    ...(values.productcategory !== "all" && {
                                        "productcategory.id": values.productcategory,
                                    }),
                                    belongs_to_sales_productcategory: true,
                                    type: "unit",
                                },
                                include: [
                                    "productVariants",
                                    "productVariants.productVariantOption",
                                ].join(","),
                        },
                        enabled: !!values.model_group,
                    },
                    conditionFunction: (
                        values,
                        orderEntryState,
                        currentCountry
                    ) => {
                        return !!values.model_group;
                    },
                }),
                ({ dispatch, state, form, send, values, queryClient, pageProps }) => {
                    // TODO: Manage this in a cleaner way
                    const products = queryClient.getQueryData(["variant", {
                        filter: {
                                model_group: values.model_group,
                                ...(values.productcategory !== "all" && {
                                    "productcategory.id": values.productcategory,
                                }),
                                belongs_to_sales_productcategory: true,
                                type: "unit",
                            },
                            include: [
                                "productVariants",
                                "productVariants.productVariantOption",
                            ].join(","),
                    }]) ?? [];
                    const productVariantOptions = uniqBy(
                        products
                            ?.flatMap((product) => product.productVariants)
                            ?.flatMap((productVariant) => productVariant?.productVariantOption)
                            ?.filter(Boolean)
                            // TODO: In this case, it will also show if not color is selected
                            ?.filter((productVariantOption) => {
                                return products.every((product) =>
                                    product.productVariants.some(
                                        (pv) => pv.productVariantOption?.key === productVariantOption.key
                                    )
                                );
                            })
                            ?.sort((a, b) => a.order - b.order) ?? [],
                        "key"
                    );
                    const isReady = productVariantOptions.every(
                        (variantOption) => values.variant?.[variantOption.key]
                    ) && products.length > 0;

                    // This is the parent product the materials belong to.
                    const parentProduct = products?.find(product => {
                        return product.model_group === values.model_group && Object.keys(values.variant ?? {}).every(key => {
                            return product.productVariants.some(pv => {
                                return pv.productVariantOption?.key === key && pv?.value === values.variant[key];
                            });
                        });
                    });

                    return {
                        size: "full",
                        component: "productselector",
                        maxQuantity: pageProps?.maxQuantity ?? 1,
                        getProductCardProps: (product => ({
                            variant: "amazon",
                        })),
                        filterProduct: (product) => {
                            return parentProduct?.materials?.some(
                                (material) => material.id === product.id
                            );
                        },
                        queryOptions: {
                            queryParameters: {
                                filter: {
                                        model_group: values.model_group,
                                        belongs_to_sales_productcategory: true,
                                        type: "unit",
                                        ...(values.productcategory !== "all" && {
                                            "productcategory.id": values.productcategory,
                                        }),
                                        variant: values.variant,
                                    },
                                include: [
                                    "materials",
                                    "productVariants",
                                    "productVariants.productVariantOption",
                                ].join(","),
                            },
                            onSuccess: (products = []) => {
                                dispatch({type: 'setSelectedCategory', value: products[0]});

                                // Always when we fetch products, we want to add the new products the our products array and initialize the quantities to 0
                                form.change(
                                    "products",
                                    uniqBy(
                                        [
                                            ...(values?.products ?? []),
                                            ...products
                                                ?.flatMap?.((product) => product.materials ?? [])
                                                ?.map((material) => ({
                                                    ...material,
                                                    amount: 0,
                                                })),
                                        ],
                                        "id"
                                    )
                                );
                            },
                            enabled: !!values.model_group && !!values.variant && isReady,
                        },
                        title: (
                            <Trans i18nKey={"orderEntry.productSelect.heading"}>
                                Please select your product
                            </Trans>
                        ),
                        conditionFunction: (
                            values,
                            orderEntryState,
                            currentCountry
                        ) => {
                            return (
                                !!values.productcategory &&
                                !!values.model_group &&
                                !!values.variant &&
                                isReady
                            );
                        },
                    }
                },
            ],
            noNextButton: true,
        },
    ],
    actions: [
        {
            fields: [
                {
                    text: <Trans i18nKey="shoppingCart.goToCheckout">Complete Checkout</Trans>,
                    size: "half",
                    component: "button",
                    color: "secondary",
                    onClick: () => {
                        Inertia.visit(route('partsshop.checkout'));
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return  hasProducts(values.products);
                    },
                },
            ],
        },
    ],
};
